import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export interface PortalProps {
  /**
   * The content or node to embed in the portal
   */
  children?: React.ReactNode;
  /* unique element id */
  id: string;
}

const createPortalDiv = (id: string): HTMLDivElement => {
  const rootContainer = document.createElement('div');
  rootContainer.setAttribute('id', id);
  return rootContainer;
}

const addRootElement = (rootElem: HTMLDivElement|Element): void => {
  const mount = document.body;
  mount.appendChild(rootElem);
}

const usePortal = (id: string): HTMLDivElement => {
  const rootElemRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Look for existing target dom element to append to
    const existingPortalDiv = document.querySelector(`#${id}`);
    // if it's not already there create one
    const portalDiv = existingPortalDiv || createPortalDiv(id);

    // If there is no existing DOM element, add a new one.
    if (!existingPortalDiv) {
      addRootElement(portalDiv);
    }

    // Add the detached element to the dom
    if (rootElemRef.current) {
      portalDiv.appendChild(rootElemRef.current);
    }

    return () => {
      if (rootElemRef.current) {
        rootElemRef.current.remove();
      }

      if (!portalDiv.childElementCount) {
        portalDiv.remove();
      }
    };
  }, [id]);

  // make sure rootElemRef is created lazily once
  // https://reactjs.org/docs/hooks-faq.html#how-to-create-expensive-objects-lazily
  const getRootElem = () => {
    if (!rootElemRef.current) {
      rootElemRef.current = document.createElement('div');
      rootElemRef.current.className = 'portal';
    }
    return rootElemRef.current;
  }

  return getRootElem();
}

/**
 *
 * Generic Portal for features like popovers and modals
 */

export const Portal = (props: PortalProps) => {
  const { children } = props;

  const target = usePortal(props.id);
  return createPortal(
    children,
    target,
  );
}
